/**
 * The wave-3 shared context: the single object every UI module (viewport, timeline, panels,
 * shell) receives at mount time.
 *
 * It bundles the four things a UI module is allowed to reach for:
 *  - `store`   — all state reads and every mutation (never write state directly),
 *  - `session` — the live three.js objects (model, bound rig) that must not enter the store,
 *  - `events`  — a tiny synchronous bus for "the world changed underneath you" notifications
 *                that are not expressible as state transitions (a new model was added to the
 *                scene graph, the skinned mesh was swapped, …),
 *  - `rigTools`— the auto-skinning side of RIG mode, implemented by the rig controller.
 *
 * This file deliberately holds no logic beyond the bus: it is the shared vocabulary of wave 3
 * and has to stay stable while several coders build against it in parallel.
 */

import type { SkinnedMesh, WebGLRenderer } from 'three';

import type { CanonicalBoneId, SkinData, SkinResolution } from '../shared';
import type { BuiltCanonicalSkeleton } from '../rig/skeletonBuilder';
import type { TestPose, TestPoseId } from '../rig/autoskin/types';
import type { EditorSession } from './session';
import type { Store } from './store';
import type { LeakSummary } from './types';

// ---------------------------------------------------------------------------
// Event bus
// ---------------------------------------------------------------------------

/**
 * Editor-wide notifications that are *not* store state.
 *
 * - `model-changed`   a different `session.model` is in play (import, project close)
 * - `rig-changed`     `session.rig` was re-bound (mapping committed, skeleton generated)
 * - `skin-changed`    the skinned mesh / skin weights changed (generate skin, paint stroke)
 * - `camera-preset`   someone asked the viewport for a camera preset (payload: `CameraPreset`)
 * - `request-frame`   ask the viewport to redraw even though nothing in the store changed
 * - `companions-changed`
 *                     `session.companions` gained or lost a read-only preview character
 *                     (`previewController`). The store carries their names, but the live roots
 *                     and rigs are session objects, so the viewport has to be told to re-attach.
 */
export type EditorEvent =
  | 'model-changed'
  | 'rig-changed'
  | 'skin-changed'
  | 'camera-preset'
  | 'request-frame'
  | 'companions-changed';

export interface EventBus {
  /** Subscribe; returns an unsubscribe function. */
  on(evt: EditorEvent, fn: (payload?: unknown) => void): () => void;
  /** Notify every current listener synchronously. */
  emit(evt: EditorEvent, payload?: unknown): void;
}

export function createEventBus(): EventBus {
  const listeners = new Map<EditorEvent, Set<(payload?: unknown) => void>>();

  return {
    on(evt, fn) {
      let set = listeners.get(evt);
      if (!set) {
        set = new Set();
        listeners.set(evt, set);
      }
      set.add(fn);
      return () => {
        set.delete(fn);
      };
    },
    emit(evt, payload) {
      const set = listeners.get(evt);
      if (!set || set.size === 0) return;
      // Copy first: a listener may unsubscribe (or subscribe) while we are dispatching.
      for (const fn of [...set]) fn(payload);
    },
  };
}

// ---------------------------------------------------------------------------
// Rig tools (implemented by the rig controller, consumed by the viewport)
// ---------------------------------------------------------------------------

/** What one generation (or re-solve) cost and how well the mesh voxelized. */
export interface SkinGenerationStats {
  resolution: SkinResolution;
  /** True when the voxel shell sealed — an open shell leaks geodesics into the outside. */
  closed: boolean;
  surface: number;
  interior: number;
  /** Voxelization, milliseconds. */
  buildMs: number;
  /** All bones' geodesic sweeps, milliseconds. */
  geodesicMs: number;
  /** The last weight solve, milliseconds. */
  solveMs: number;
  /** Bones whose segment endpoints had to be pulled into the voxel domain. */
  pulledInside: CanonicalBoneId[];
  /** Unique positions no bone could reach geodesically (the Euclidean fallback carried them). */
  unreachable: number;
  fallbackVertices: number;
  nonFinite: number;
  vertexCount: number;
  uniqueCount: number;
}

/**
 * The auto-skinning half of RIG mode (auto-skin v2). The viewport and the SKIN panel only
 * *display* the result and ask for corrections; voxelizing, walking the geodesics, solving the
 * weights and swapping the mesh happen behind this interface.
 *
 * Every correction entry point tolerates a reloaded project, where the weights were restored from
 * disk and no distance cache exists yet: they `ensureCache()` first (and flip `rig.skinBusy` while
 * that runs).
 */
export interface RigTools {
  /**
   * Build the skeleton from `store.getState().rig.placement`, build a fresh distance cache, solve
   * with `store.getState().rig.skinParams`, swap in the SkinnedMesh(es), then emit
   * `'skin-changed'` + `'rig-changed'` and save. Sets `rig.skinBusy` around the heavy part.
   */
  generateSkin(): Promise<void>;
  /**
   * Build the geodesic distance cache for the current placement + resolution if there is none
   * (after a reload, or after a resolution change). No-op when the cache is already current.
   */
  ensureCache(): Promise<void>;
  hasCache(): boolean;
  /**
   * Re-solve every mesh from the cached distances with the current `rig.skinParams` and write the
   * weights into the live geometry; emits `'skin-changed'`. Runs automatically on every
   * `rig.skinParams` change (coalesced), so a panel rarely needs to call it.
   */
  resolveFromCache(): Promise<void>;
  /**
   * Deform the mesh with one test pose, report the vertices that moved but should not have, and
   * write the summary to `rig.leakSummary`. Null when there is no skin.
   */
  runLeakCheck(poseId: TestPoseId): Promise<LeakSummary | null>;
  /**
   * Shrink the moved bones' offsets until the leak count stops improving, apply the best
   * parameters found, re-solve and re-check. `revertLastFix()` undoes it.
   */
  fixLeaks(poseId: TestPoseId): Promise<LeakSummary | null>;
  /** Restore the parameters from before the last `fixLeaks` and re-solve. No-op if there was none. */
  revertLastFix(): Promise<void>;
  /**
   * One-click region reassignment: the connected region the clicked vertex belongs to is pinned
   * to `boneId` through `skinParams.regionOverrides`, then the mesh is re-solved. Clicking a
   * region twice replaces the earlier override rather than stacking one on top of it.
   */
  reassignRegionAt(meshIndex: number, vertexIndex: number, boneId: CanonicalBoneId): Promise<void>;
  /** Drop every region override and re-solve. */
  clearRegionOverrides(): Promise<void>;
  /** Per-vertex `1` where the last leak check found a leak, else `0`. Null before any check. */
  leakMask(meshIndex?: number): Float32Array | null;
  /** Per-vertex influence of one bone index, for heat display. Null before skin generation. */
  influenceMask(boneIndex: number, meshIndex?: number): Float32Array | null;
  /** Cost + quality of the current cache and the last solve. Null before generation. */
  skinStats(): SkinGenerationStats | null;
  /** The test-pose gallery, so panels never import a domain module. */
  testPoses(): readonly TestPose[];
  /**
   * True when the skin parameters differ from the defaults, or the weights were restored from a
   * saved project. Regenerating discards those corrections, so the RIG panel confirms first.
   */
  hasSkinEdits(): boolean;
  /**
   * Persist the project state immediately, cancelling the debounce. Fire-and-forget: called at
   * the end of a joint drag, because `beforeunload` cannot await IndexedDB.
   */
  saveNow(): void;
  /** The live skinned mesh, or null when skin has not been generated yet. */
  skinnedMesh(meshIndex?: number): SkinnedMesh | null;
  /** The current skin weights, or null before generation. */
  skinData(meshIndex?: number): SkinData | null;
  /** The canonical skeleton built from the placement, or null before generation. */
  builtSkeleton(): BuiltCanonicalSkeleton | null;
}

// ---------------------------------------------------------------------------
// Context + viewport handle
// ---------------------------------------------------------------------------

export interface EditorContext {
  store: Store;
  session: EditorSession;
  events: EventBus;
  rigTools: RigTools;
}

/** Fixed camera viewpoints offered by the viewport (keys `5`, `1`, `3`, `7`). */
export type CameraPreset = 'persp' | 'front' | 'side' | 'top';

export interface ViewportHandle {
  dispose(): void;
  resize(): void;
  setCameraPreset(preset: CameraPreset): void;
  /** Fit the camera to the current model's bounds. */
  frameModel(): void;
  renderer: WebGLRenderer;
}
